import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background,
  Panel,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import InvoiceNode from './InvoiceNode';
import './InvoiceRelationshipModal.css';
import invoicingService from '../../services/invoicingService';
import tradeRecapService from '../../services/tradeRecapService';
import deliveryService from '../../services/deliveryService';

/**
 * Modal component for displaying document relationship graph
 * Shows connections between:
 * - Invoicing: proforma/outgoing/credit notes or PO/incoming/debit notes
 * - Trade Operations: deal/recap/delivery
 */
const InvoiceRelationshipModal = ({ isOpen, onClose, invoice, invoiceType, document, documentType }) => {
  // Use either invoice/invoiceType or document/documentType
  const activeDocument = document || invoice;
  const activeType = documentType || invoiceType;
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const history = useHistory();

  // Define custom node types with navigation - memoized to prevent unnecessary re-renders
  const nodeTypes = useMemo(() => ({
    invoiceNode: (props) => <InvoiceNode {...props} history={history} />
  }), [history]);

  // Layout constants for collision-free positioning
  const LAYOUT_CONFIG = {
    NODE_WIDTH: 320,           // Max node width + padding
    NODE_HEIGHT: 180,          // Estimated average node height
    MIN_HORIZONTAL_SPACING: 380,  // Minimum space between nodes horizontally
    MIN_VERTICAL_SPACING: 150,    // Minimum space between nodes vertically (for products)
    BASE_PRODUCT_OFFSET_X: 450,   // Base distance to the left for product nodes
    CENTER_X: 400,                // Center point for layout
    LAYER_Y: {
      TOP: 50,
      MIDDLE: 400,              // Increased from 320 to 400 (80px more space)
      BOTTOM: 750               // Increased from 590 to 750 (160px more space)
    }
  };

  // Helper function to detect if two nodes overlap
  const nodesOverlap = (node1, node2) => {
    const box1 = {
      left: node1.position.x,
      right: node1.position.x + LAYOUT_CONFIG.NODE_WIDTH,
      top: node1.position.y,
      bottom: node1.position.y + LAYOUT_CONFIG.NODE_HEIGHT
    };
    
    const box2 = {
      left: node2.position.x,
      right: node2.position.x + LAYOUT_CONFIG.NODE_WIDTH,
      top: node2.position.y,
      bottom: node2.position.y + LAYOUT_CONFIG.NODE_HEIGHT
    };
    
    return !(box1.right < box2.left || 
             box1.left > box2.right || 
             box1.bottom < box2.top || 
             box1.top > box2.bottom);
  };

  // Helper function to adjust overlapping nodes
  const resolveOverlaps = (nodes) => {
    const maxIterations = 50;
    let iteration = 0;
    let hasOverlap = true;
    
    while (hasOverlap && iteration < maxIterations) {
      hasOverlap = false;
      
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          if (nodesOverlap(nodes[i], nodes[j])) {
            hasOverlap = true;
            
            // Shift nodes apart based on their relative positions
            const dx = nodes[j].position.x - nodes[i].position.x;
            const dy = nodes[j].position.y - nodes[i].position.y;
            
            if (Math.abs(dx) > Math.abs(dy)) {
              // Shift horizontally
              const shift = (LAYOUT_CONFIG.NODE_WIDTH + 30) / 2;
              if (dx > 0) {
                nodes[j].position.x += shift;
              } else {
                nodes[j].position.x -= shift;
              }
            } else {
              // Shift vertically
              const shift = (LAYOUT_CONFIG.NODE_HEIGHT + 30) / 2;
              if (dy > 0) {
                nodes[j].position.y += shift;
              } else {
                nodes[j].position.y -= shift;
              }
            }
          }
        }
      }
      iteration++;
    }
    
    return nodes;
  };

  // Calculate dynamic spacing based on node count
  const calculateDynamicSpacing = (nodeCount) => {
    if (nodeCount === 1) return 0;
    // Ensure minimum spacing while allowing more space for fewer nodes
    const baseSpacing = Math.max(LAYOUT_CONFIG.MIN_HORIZONTAL_SPACING, 500 / nodeCount);
    return Math.min(baseSpacing, 600); // Cap at 600px for very few nodes
  };

  // Calculate node positions based on type and relationships
  const calculateNodePositions = (nodesData, edgesData) => {
    const layers = {
      top: [],      // Proforma or PO or Deal
      middle: [],   // Outgoing or Incoming or Recap
      bottom: [],   // Credit Notes or Debit Notes or Delivery
      products: []  // Product nodes (positioned to the left)
    };

    // Categorize nodes by layer
    nodesData.forEach(node => {
      if (node.type === 'PRODUCT') {
        layers.products.push(node);
      } else if (node.type === 'PROFORMA' || node.type === 'PURCHASE_ORDER' || node.type === 'DEAL') {
        layers.top.push(node);
      } else if (node.type === 'OUTGOING' || node.type === 'INCOMING' || node.type === 'RECAP' || node.type === 'FREIGHT_RECAP') {
        layers.middle.push(node);
      } else if (node.type === 'CREDIT_NOTE' || node.type === 'DEBIT_NOTE' || node.type === 'DELIVERY') {
        layers.bottom.push(node);
      }
    });

    const positioned = [];
    
    // Determine which layer products will connect to (for offset calculation)
    let targetLayerCount = 0;
    if (layers.products.length > 0 && edgesData) {
      // Find what type of nodes products connect to
      const productEdge = edgesData.find(edge => 
        layers.products.some(p => p.id === edge.source)
      );
      if (productEdge) {
        const targetNode = nodesData.find(n => n.id === productEdge.target);
        if (targetNode) {
          if (targetNode.type === 'DEAL' || targetNode.type === 'PROFORMA' || targetNode.type === 'PURCHASE_ORDER') {
            targetLayerCount = layers.top.length;
          } else if (targetNode.type === 'RECAP' || targetNode.type === 'FREIGHT_RECAP' || targetNode.type === 'OUTGOING' || targetNode.type === 'INCOMING') {
            targetLayerCount = layers.middle.length;
          } else if (targetNode.type === 'DELIVERY' || targetNode.type === 'CREDIT_NOTE' || targetNode.type === 'DEBIT_NOTE') {
            targetLayerCount = layers.bottom.length;
          }
        }
      }
    }
    
    // Calculate dynamic product offset based on target layer node count and spacing
    const targetLayerSpacing = targetLayerCount > 0 ? calculateDynamicSpacing(targetLayerCount) : LAYOUT_CONFIG.MIN_HORIZONTAL_SPACING;
    // Product offset must be larger than node width + spacing to avoid overlaps
    const dynamicProductOffset = Math.max(
      LAYOUT_CONFIG.BASE_PRODUCT_OFFSET_X,
      LAYOUT_CONFIG.NODE_WIDTH + targetLayerSpacing + 50  // Add 50px safety buffer
    );

    // Position top layer with dynamic spacing
    if (layers.top.length > 0) {
      const topSpacing = calculateDynamicSpacing(layers.top.length);
      const topStartX = LAYOUT_CONFIG.CENTER_X - ((layers.top.length - 1) * topSpacing / 2);
      
      layers.top.forEach((node, idx) => {
        positioned.push({
          id: node.id,
          type: 'invoiceNode',
          position: { 
            x: topStartX + (idx * topSpacing) - 125, 
            y: LAYOUT_CONFIG.LAYER_Y.TOP 
          },
          data: node
        });
      });
    }

    // Position middle layer with dynamic spacing
    if (layers.middle.length > 0) {
      const middleSpacing = calculateDynamicSpacing(layers.middle.length);
      const middleStartX = LAYOUT_CONFIG.CENTER_X - ((layers.middle.length - 1) * middleSpacing / 2);
      
      layers.middle.forEach((node, idx) => {
        positioned.push({
          id: node.id,
          type: 'invoiceNode',
          position: { 
            x: middleStartX + (idx * middleSpacing) - 125, 
            y: LAYOUT_CONFIG.LAYER_Y.MIDDLE 
          },
          data: node
        });
      });
    }

    // Position bottom layer with dynamic spacing
    if (layers.bottom.length > 0) {
      const bottomSpacing = calculateDynamicSpacing(layers.bottom.length);
      const bottomStartX = LAYOUT_CONFIG.CENTER_X - ((layers.bottom.length - 1) * bottomSpacing / 2);
      
      layers.bottom.forEach((node, idx) => {
        positioned.push({
          id: node.id,
          type: 'invoiceNode',
          position: { 
            x: bottomStartX + (idx * bottomSpacing) - 125, 
            y: LAYOUT_CONFIG.LAYER_Y.BOTTOM 
          },
          data: node
        });
      });
    }

    // Position product nodes to the left of their connected nodes with proper vertical spacing
    if (layers.products.length > 0 && edgesData) {
      const productsByTarget = {};
      
      // Group products by their target node
      layers.products.forEach(productNode => {
        const productEdge = edgesData.find(edge => edge.source === productNode.id);
        if (productEdge) {
          const targetId = productEdge.target;
          if (!productsByTarget[targetId]) {
            productsByTarget[targetId] = [];
          }
          productsByTarget[targetId].push(productNode);
        }
      });
      
      // Find the leftmost X position in the target layer to position products safely to the left
      let targetLayerMinX = Infinity;
      Object.keys(productsByTarget).forEach(targetId => {
        const targetNode = positioned.find(p => p.id === targetId);
        if (targetNode && targetNode.position.x < targetLayerMinX) {
          targetLayerMinX = targetNode.position.x;
        }
      });
      
      // Calculate safe X position for products column (left of all target layer nodes)
      const productsColumnX = targetLayerMinX !== Infinity 
        ? targetLayerMinX - dynamicProductOffset 
        : 50;
      
      // Position each product group with vertical spacing to prevent overlaps
      let productYOffset = 0;
      Object.keys(productsByTarget).forEach(targetId => {
        const targetNode = positioned.find(p => p.id === targetId);
        const products = productsByTarget[targetId];
        
        if (targetNode) {
          // Position products in a vertical column, aligned to target's Y but with offset
          products.forEach((productNode, idx) => {
            positioned.push({
              id: productNode.id,
              type: 'invoiceNode',
              position: { 
                x: productsColumnX,
                y: targetNode.position.y + productYOffset + (idx * LAYOUT_CONFIG.MIN_VERTICAL_SPACING)
              },
              data: productNode
            });
          });
          // Increment offset for next product group
          productYOffset += products.length * LAYOUT_CONFIG.MIN_VERTICAL_SPACING;
        } else {
          // Fallback: position in left column with proper spacing
          products.forEach((productNode, idx) => {
            positioned.push({
              id: productNode.id,
              type: 'invoiceNode',
              position: { 
                x: 50, 
                y: 50 + productYOffset + (idx * LAYOUT_CONFIG.MIN_VERTICAL_SPACING) 
              },
              data: productNode
            });
          });
          productYOffset += products.length * LAYOUT_CONFIG.MIN_VERTICAL_SPACING;
        }
      });
    }

    // Apply collision detection and resolution
    const finalPositions = resolveOverlaps(positioned);
    
    return finalPositions;
  };

  // Format edges with proper styling
  const formatEdges = (edgesData) => {
    return edgesData.map(edge => {
      // Determine color and style based on edge type
      let strokeColor = '#4a6b25'; // default blue for conversion
      let strokeDasharray = undefined; // solid line by default
      
      if (edge.type === 'adjustment') {
        strokeColor = '#ef4444'; // red for adjustments
      } else if (edge.type === 'fulfillment') {
        strokeColor = '#8b5cf6'; // purple for fulfillment
      } else if (edge.type === 'product') {
        strokeColor = '#ffc20c'; // amazon orange for products
        strokeDasharray = '5,5'; // dotted line for products
      }
      
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        type: 'smoothstep',
        animated: edge.type === 'conversion',
        style: { 
          stroke: strokeColor,
          strokeWidth: 2,
          strokeDasharray: strokeDasharray
        },
        labelStyle: {
          fill: '#fff',
          fontWeight: 500,
          fontSize: 12
        },
        labelBgStyle: {
          fill: strokeColor,
          fillOpacity: 0.9
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: strokeColor,
          width: 20,
          height: 20
        }
      };
    });
  };

  // Fetch relationship data
  useEffect(() => {
    if (!isOpen || !activeDocument) return;

    const fetchRelationships = async () => {
      setLoading(true);
      setError(null);

      try {
        let data;
        
        switch (activeType) {
          // Invoicing types
          case 'outgoing':
            data = await invoicingService.getOutgoingInvoiceRelationships(activeDocument.id);
            break;
          case 'proforma':
            data = await invoicingService.getProformaInvoiceRelationships(activeDocument.id);
            break;
          case 'credit':
            data = await invoicingService.getCreditNoteRelationships(activeDocument.id);
            break;
          case 'po':
            data = await invoicingService.getPurchaseOrderRelationships(activeDocument.id);
            break;
          case 'incoming':
            data = await invoicingService.getIncomingInvoiceRelationships(activeDocument.id);
            break;
          case 'debit':
            data = await invoicingService.getDebitNoteRelationships(activeDocument.id);
            break;
          // Trade operations types
          case 'deal':
            data = await tradeRecapService.getDealRelationships(activeDocument.offer_id);
            break;
          case 'recap':
            data = await tradeRecapService.getRecapRelationships(activeDocument.id);
            break;
          case 'freight_recap':
            data = await tradeRecapService.getFreightRecapRelationships(activeDocument.id);
            break;
          case 'delivery':
            data = await deliveryService.getDeliveryRelationships(activeDocument.id);
            break;
          default:
            throw new Error('Unknown document type');
        }

        // Calculate positions and set nodes (pass edges for product positioning)
        const positionedNodes = calculateNodePositions(data.nodes, data.edges);
        setNodes(positionedNodes);

        // Format and set edges
        const formattedEdges = formatEdges(data.edges);
        setEdges(formattedEdges);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching relationships:', err);
        setError('Failed to load trade operations relationships. Please try again.');
        setLoading(false);
      }
    };

    fetchRelationships();
  }, [isOpen, activeDocument, activeType]);

  // Handle close
  const handleClose = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="invoice-relationship-modal-overlay" onClick={handleClose}>
      <div className="invoice-relationship-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="invoice-relationship-modal-header">
          <h2>Trade Operations Relationships</h2>
          <button className="invoice-relationship-close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="invoice-relationship-modal-content">
          {loading && (
            <div className="invoice-relationship-loading">
              <div className="loading-spinner"></div>
              <p>Loading relationships...</p>
            </div>
          )}

          {error && (
            <div className="invoice-relationship-error">
              <p>{error}</p>
              <button onClick={handleClose}>Close</button>
            </div>
          )}

          {!loading && !error && (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              minZoom={0.5}
              maxZoom={1.5}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            >
              <Background color="#aaa" gap={16} />
              <Controls showInteractive={false} />
              <MiniMap 
                nodeColor={(node) => node.data.color}
                maskColor="rgba(0, 0, 0, 0.2)"
              />
              
              {/* Legend inside ReactFlow */}
              <Panel position="top-left" className="invoice-relationship-legend-panel">
                <div className="invoice-relationship-legend">
                  <div className="legend-title">Legend:</div>
                  <div className="legend-items">
                    <div className="legend-item">
                      <div className="legend-arrow conversion"></div>
                      <span>Conversion</span>
                    </div>
                    {(activeType === 'deal' || activeType === 'recap' || activeType === 'freight_recap' || activeType === 'delivery') ? (
                      <>
                        <div className="legend-item">
                          <div className="legend-arrow fulfillment"></div>
                          <span>Fulfillment</span>
                        </div>
                        <div className="legend-item">
                          <div className="legend-arrow product"></div>
                          <span>Product</span>
                        </div>
                      </>
                    ) : (
                      <div className="legend-item">
                        <div className="legend-arrow adjustment"></div>
                        <span>Adjustment</span>
                      </div>
                    )}
                  </div>
                </div>
              </Panel>
            </ReactFlow>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceRelationshipModal;
